<section class="query-test-page">
  <header class="query-test-header">
    <div>
      @if (!embedded) {
        <p class="eyebrow">Shine AI diagnostics</p>
      }
      <h1>Product query tester</h1>
      <p class="header-copy">
        Run a raw Lucene query against
        @if (embedded && productFeed) {
          <span>{{ queryScopeLabel }}</span>
        } @else {
          <span>the product index</span>
        }
        and inspect the top results.
      </p>
    </div>
  </header>

  <form class="query-panel" [formGroup]="form" (ngSubmit)="runQuery()">
    <mat-form-field appearance="outline" class="query-field">
      <mat-label>Lucene query</mat-label>
      <textarea
        matInput
        formControlName="query"
        rows="3"
        spellcheck="false"
        placeholder='terms:*ull* AND size:m AND NOT availability:"out of stock"'>
      </textarea>
      @if (form.controls.query.hasError('required') && form.controls.query.touched) {
        <mat-error>Query is required</mat-error>
      }
    </mat-form-field>

    <div class="filter-grid">
      <mat-form-field appearance="outline">
        <mat-label>Categories</mat-label>
        <input matInput formControlName="categories" placeholder="comma,separated,category,boosts">
      </mat-form-field>

      @if (!embedded) {
        <mat-form-field appearance="outline">
          <mat-label>Product feeds</mat-label>
          <mat-select formControlName="productFeedIds" multiple>
            @for (feed of productFeeds; track feed.id) {
              <mat-option [value]="feed.id">
                {{ feed.name || feed.id }}
                @if (feed.market) {
                  <span class="muted-option">({{ feed.market }})</span>
                }
              </mat-option>
            }
          </mat-select>
          @if (loadingFeeds) {
            <mat-hint>Loading feeds</mat-hint>
          }
        </mat-form-field>
      }

      <mat-form-field appearance="outline">
        <mat-label>Market label</mat-label>
        <input matInput formControlName="market" placeholder="Norway">
      </mat-form-field>

      <mat-form-field appearance="outline">
        <mat-label>Limit</mat-label>
        <input matInput formControlName="limit" type="number" min="1" max="50">
      </mat-form-field>
    </div>

    <div class="query-actions">
      <button mat-flat-button color="primary" type="submit" [disabled]="loading || form.invalid">
        @if (loading) {
          <mat-spinner diameter="18"></mat-spinner>
        }
        <span>Run query</span>
      </button>
      <button mat-button type="button" (click)="clear()" [disabled]="loading">Clear</button>
    </div>
  </form>

  @if (errorMessage) {
    <div class="query-alert" role="alert">{{ errorMessage }}</div>
  }

  @if (response) {
    <section class="results-panel">
      <header>
        <div>
          <h2>Top {{ response.count }} results</h2>
          <span>{{ response.query }}</span>
        </div>
        <span class="result-meta">limit {{ response.limit }}</span>
      </header>

      @if (!response.results.length) {
        <div class="empty-state">No products matched this query.</div>
      } @else {
        <div class="result-table-wrap">
          <table mat-table [dataSource]="response.results" class="result-table">
            <ng-container matColumnDef="rank">
              <th mat-header-cell *matHeaderCellDef>#</th>
              <td mat-cell *matCellDef="let result">{{ result.rank }}</td>
            </ng-container>

            <ng-container matColumnDef="product">
              <th mat-header-cell *matHeaderCellDef>Product</th>
              <td mat-cell *matCellDef="let result">
                <div class="product-cell">
                  <strong>{{ productLabel(result) }}</strong>
                  <span>{{ result.sku || 'No SKU' }}</span>
                  @if (result.url) {
                    <a [href]="result.url" target="_blank" rel="noreferrer">Open</a>
                  }
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="constraints">
              <th mat-header-cell *matHeaderCellDef>Matched fields</th>
              <td mat-cell *matCellDef="let result">
                <div class="field-stack">
                  <span>category: {{ result.category || '-' }}</span>
                  <span>availability: {{ result.availability || '-' }}</span>
                  <span>size: {{ result.size || '-' }}</span>
                  <span>color: {{ result.color || '-' }}</span>
                  <span>price: {{ result.salePrice || result.price || '-' }}</span>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="feed">
              <th mat-header-cell *matHeaderCellDef>Feed</th>
              <td mat-cell *matCellDef="let result">
                <div class="field-stack">
                  <span>{{ result.productFeedName || result.productFeedId || '-' }}</span>
                  <span>{{ result.market || '-' }}</span>
                </div>
              </td>
            </ng-container>

            <ng-container matColumnDef="score">
              <th mat-header-cell *matHeaderCellDef>Score</th>
              <td mat-cell *matCellDef="let result">{{ result.searchRelevance | number:'1.2-2' }}</td>
            </ng-container>

            <ng-container matColumnDef="actions">
              <th mat-header-cell *matHeaderCellDef></th>
              <td mat-cell *matCellDef="let result">
                <button mat-icon-button type="button" (click)="toggleRaw(result)" [attr.aria-label]="'Toggle raw JSON for ' + productLabel(result)">
                  <mat-icon>{{ expandedRawByRank[result.rank] ? 'expand_less' : 'expand_more' }}</mat-icon>
                </button>
              </td>
            </ng-container>

            <tr mat-header-row *matHeaderRowDef="displayedColumns"></tr>
            <tr mat-row *matRowDef="let row; columns: displayedColumns;"></tr>
          </table>
        </div>

        <div class="raw-results">
          @for (result of response.results; track trackResult($index, result)) {
            @if (expandedRawByRank[result.rank]) {
              <section class="raw-panel">
                <header>
                  <strong>#{{ result.rank }} {{ productLabel(result) }}</strong>
                </header>
                <pre>{{ rawJson(result) }}</pre>
              </section>
            }
          }
        </div>
      }
    </section>
  }
</section>
